// src/pages/trading-portal/TradeExecution/AIAgentPositionsPage.js
// AI Agent Positions — every open position held by the team's AI trading
// agents, across all agents or filtered to one.
//
// Positions are derived server-side from `_walk_agent_fills`, the single
// definition of "the position" for an AI agent, so this page cannot disagree
// with the agent's own Position tab, its LLM prompt, or PureCodeRouter.
//
// Quantities are floats, never integers: a 0.4 BTC position is real, and the
// blackboard's int-truncating `open_positions` would render it as 0.

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Skeleton, Alert, Grid, Chip, FormControl, InputLabel,
    Select, MenuItem, Button, Tooltip, Switch, FormControlLabel,
} from '@mui/material';
import * as MuiIcons from '@mui/icons-material';
import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const POSITIVE = '#16a34a';
const NEGATIVE = '#dc2626';

const headCellSx = {
    fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.72rem',
    textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap',
};
const bodyCellSx = { fontFamily, fontSize: '0.82rem', color: '#334155', whiteSpace: 'nowrap' };

const MODULE_SLUG = 'tr-tee-ai-agent-positions';
const ALL_AGENTS = 'ALL';
const REFRESH_MS = 30000;

// Every formatter returns an em dash for null/undefined rather than 0 — the
// backend leaves market-dependent fields null when no price could be resolved,
// and rendering those as $0.00 would report a fictitious loss.
const money = (v, dp = 2) => {
    if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
    const n = Number(v);
    return `${n < 0 ? '-' : ''}$${Math.abs(n).toLocaleString(undefined, {
        minimumFractionDigits: dp, maximumFractionDigits: dp,
    })}`;
};

const percent = (v) => {
    if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
    return `${Number(v).toFixed(2)}%`;
};

// Spot crypto trades fractionally, down to 9 decimals at the broker; equities are
// whole shares. Same rule the agent sidebar's Martingale ladder uses.
const quantity = (v, isSpotCrypto) => {
    if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
    const n = Math.abs(Number(v));
    return n.toLocaleString(undefined, { maximumFractionDigits: isSpotCrypto ? 9 : 2 });
};

const signColor = (v) => {
    if (v === null || v === undefined || Number(v) === 0) return '#334155';
    return Number(v) > 0 ? POSITIVE : NEGATIVE;
};

// Timestamps arrive offset-aware from the API; pin the zone to the agent's
// trading timezone so a fill time lines up with the agent's trading day rather
// than the reader's. Falls back to viewer-local on a bad IANA name, because
// toLocaleString throws a RangeError instead of degrading.
const formatInAgentTz = (iso, tzName) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    if (!tzName) return d.toLocaleString();
    try {
        return d.toLocaleString(undefined, { timeZone: tzName });
    } catch (e) {
        return d.toLocaleString();
    }
};

// What actually served the price on this row. Shown so a stale figure is never
// passed off as live.
const PRICE_SOURCE_META = {
    STREAM: { label: 'Live', color: '#dcfce7', text: '#15803d', help: 'Live Alpaca tick — at most one trade old' },
    HISTORY: { label: 'Bar', color: '#e0f2fe', text: '#0369a1', help: 'Newest stored price bar — up to one bar behind' },
    SYMBOL: { label: 'Cached', color: '#fef3c7', text: '#a16207', help: 'Scheduler-written symbol price — may be stale' },
    NONE: { label: 'No price', color: '#fee2e2', text: '#b91c1c', help: 'No price available; market value and gain/loss cannot be computed' },
};

const SummaryCard = ({ label, value, sub, color }) => (
    <Paper sx={{ p: 2, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
        <Typography sx={{
            fontFamily, fontSize: '0.7rem', fontWeight: 600, color: '#94a3b8',
            textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5,
        }}>
            {label}
        </Typography>
        <Typography sx={{ fontFamily, fontSize: '1.35rem', fontWeight: 700, color: color || '#1e293b' }}>
            {value}
        </Typography>
        {sub && (
            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8', mt: 0.25 }}>
                {sub}
            </Typography>
        )}
    </Paper>
);

const AIAgentPositionsPage = () => {
    const [canView, setCanView] = useState(false);
    const [permChecked, setPermChecked] = useState(false);

    const [selectedAgentId, setSelectedAgentId] = useState(ALL_AGENTS);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [lastRefreshed, setLastRefreshed] = useState(null);

    useEffect(() => {
        (async () => {
            // Module slug + bare permission name. Some sibling pages pass the
            // display name and 'can_view', which resolves 'can_can_view' and is
            // wrong — this is the correct form.
            setCanView(await hasPermission(MODULE_SLUG, 'view'));
            setPermChecked(true);
        })();
    }, []);

    // Guards against the two ways a polling table goes wrong:
    //   inFlight  — a request slower than the refresh interval would otherwise
    //               have the next tick stack another on top of it, so a slow
    //               backend gets amplified by every open tab instead of
    //               throttled. Background ticks skip `loading`, so the Refresh
    //               button's `disabled={loading}` does not cover this.
    //   seq       — responses can resolve out of order; without a sequence
    //               check an older snapshot can overwrite a newer one and sit
    //               there under a fresh "Updated HH:MM:SS" stamp.
    const inFlight = useRef(false);
    const seq = useRef(0);

    const loadPositions = useCallback(async ({ background = false } = {}) => {
        if (background && inFlight.current) return;
        const mySeq = ++seq.current;
        inFlight.current = true;
        // `background: true` skips the loading state so an auto-refresh tick
        // leaves the current rows on screen instead of flashing skeletons.
        if (!background) setLoading(true);
        setError('');
        try {
            const q = selectedAgentId !== ALL_AGENTS ? `?agent_id=${selectedAgentId}` : '';
            const res = await axiosInstance.get(`/api/trade-execution/ai-agents/positions${q}`);
            if (mySeq !== seq.current) return;   // a newer request already landed
            setData(res.data?.data || null);
            setLastRefreshed(new Date());
        } catch (err) {
            if (mySeq !== seq.current) return;
            setError(err.response?.data?.error || 'Failed to load positions');
            if (!background) setData(null);
        } finally {
            if (mySeq === seq.current) inFlight.current = false;
            if (!background) setLoading(false);
        }
    }, [selectedAgentId]);

    useEffect(() => { loadPositions(); }, [loadPositions]);

    // Positions move with the market, so the page polls. Background reloads keep
    // the table populated between ticks. Skipped while the tab is hidden —
    // nobody is reading it, and the endpoint is not cheap.
    useEffect(() => {
        if (!autoRefresh) return undefined;
        const id = setInterval(() => {
            if (document.hidden) return;
            loadPositions({ background: true });
        }, REFRESH_MS);
        return () => clearInterval(id);
    }, [autoRefresh, loadPositions]);

    const positions = useMemo(() => data?.positions || [], [data]);
    const totals = data?.totals;
    const agents = data?.agents || [];
    const showAgentColumn = selectedAgentId === ALL_AGENTS;

    // The backend leaves price-dependent fields null rather than zeroing them,
    // so say plainly when a total covers only part of the book.
    const unpriced = totals ? totals.position_count - totals.priced_positions : 0;

    if (permChecked && !canView) {
        return <Alert severity="warning" sx={{ fontFamily }}>
            You do not have permission to view AI Agent Positions.
        </Alert>;
    }

    const colCount = showAgentColumn ? 16 : 15;

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.4rem', color: '#1e293b' }}>
                    AI Agent Positions
                </Typography>
                <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#94a3b8' }}>
                    Open positions held by AI trading agents, with live market value and gain/loss
                </Typography>
            </Box>

            {/* Controls */}
            <Paper sx={{ p: 2, mb: 2, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                        <FormControl size="small" fullWidth>
                            <InputLabel sx={{ fontFamily }}>Agent</InputLabel>
                            <Select label="Agent" value={selectedAgentId} sx={{ fontFamily }}
                                onChange={(e) => setSelectedAgentId(e.target.value)}>
                                <MenuItem value={ALL_AGENTS} sx={{ fontFamily }}>All agents</MenuItem>
                                {agents.map((a) => (
                                    <MenuItem key={a.id} value={a.id} sx={{ fontFamily }}>
                                        {a.name} ({a.symbol})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={8}>
                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', justifyContent: { md: 'flex-end' } }}>
                            {lastRefreshed && (
                                <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8' }}>
                                    Updated {lastRefreshed.toLocaleTimeString()}
                                </Typography>
                            )}
                            <FormControlLabel
                                control={<Switch size="small" checked={autoRefresh}
                                    onChange={(e) => setAutoRefresh(e.target.checked)} />}
                                label={`Auto-refresh (${REFRESH_MS / 1000}s)`}
                                sx={{ '& .MuiFormControlLabel-label': { fontFamily, fontSize: '0.8rem', color: '#64748b' } }} />
                            <Button size="small" variant="outlined" startIcon={<MuiIcons.Refresh />}
                                onClick={() => loadPositions()} disabled={loading}
                                sx={{ fontFamily, textTransform: 'none', borderRadius: '10px' }}>
                                Refresh
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {error && <Alert severity="error" sx={{ mb: 2, fontFamily }}>{error}</Alert>}

            {/* Portfolio summary */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <SummaryCard label="Market Value" value={money(totals?.market_value)}
                        sub={totals ? `${totals.position_count} position${totals.position_count === 1 ? '' : 's'} · ${totals.agent_count} agent${totals.agent_count === 1 ? '' : 's'}` : null} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <SummaryCard label="Cost Basis" value={money(totals?.cost_basis)}
                        sub={totals ? `${totals.long_count} long · ${totals.short_count} short` : null} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <SummaryCard label="Total Gain / Loss" value={money(totals?.total_gain_loss)}
                        color={signColor(totals?.total_gain_loss)}
                        sub={totals?.total_gain_loss_pct !== null && totals?.total_gain_loss_pct !== undefined
                            ? percent(totals.total_gain_loss_pct) : null} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <SummaryCard label="Day Gain / Loss" value={money(totals?.day_gain_loss)}
                        color={signColor(totals?.day_gain_loss)}
                        sub="vs previous close" />
                </Grid>
            </Grid>

            {data?.errored_agents?.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2, fontFamily }}>
                    {data.errored_agents.length} agent
                    {data.errored_agents.length === 1 ? '' : 's'} could not be read
                    ({data.errored_agents.map((a) => a.agent_name).join(', ')}).
                    {' '}Any position {data.errored_agents.length === 1 ? 'it holds is' : 'they hold are'} missing
                    from this table and from the totals — {data.errored_agents.length === 1 ? 'it is' : 'they are'} not
                    {' '}being reported as flat. Check the server log.
                </Alert>
            )}

            {unpriced > 0 && (
                <Alert severity="info" sx={{ mb: 2, fontFamily }}>
                    {unpriced} position{unpriced === 1 ? '' : 's'} could not be priced.
                    {' '}Cost Basis still covers {unpriced === 1 ? 'it' : 'them'} — that needs no market
                    price — but Market Value and both Gain / Loss figures do not.
                    {' '}Gain / Loss % is measured against the {money(totals?.priced_cost_basis)} basis of the
                    {' '}priced positions only, so the ratio stays like-for-like.
                </Alert>
            )}

            {/* Positions table */}
            <TableContainer component={Paper}
                sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 1500 }}>
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f8fafc' }}>
                            {showAgentColumn && <TableCell sx={headCellSx}>Agent</TableCell>}
                            <TableCell sx={headCellSx}>Symbol</TableCell>
                            <TableCell sx={headCellSx}>Description</TableCell>
                            <TableCell sx={headCellSx}>Side</TableCell>
                            <TableCell sx={headCellSx} align="right">Quantity</TableCell>
                            <TableCell sx={headCellSx} align="right">Cost / Share</TableCell>
                            <TableCell sx={headCellSx} align="right">Cost Basis</TableCell>
                            <TableCell sx={headCellSx} align="right">Last Price</TableCell>
                            <TableCell sx={headCellSx} align="right">Price Value</TableCell>
                            <TableCell sx={headCellSx} align="right">Price Change $</TableCell>
                            <TableCell sx={headCellSx} align="right">Price Change %</TableCell>
                            <TableCell sx={headCellSx} align="right">Day Gain / Loss</TableCell>
                            <TableCell sx={headCellSx} align="right">Gain / Loss</TableCell>
                            <TableCell sx={headCellSx} align="right">Gain / Loss %</TableCell>
                            <TableCell sx={headCellSx} align="right">
                                <Tooltip title="This position's share of total market value across the rows shown">
                                    <span>Weight</span>
                                </Tooltip>
                            </TableCell>
                            <TableCell sx={headCellSx} align="right">
                                <Tooltip title="Martingale ladder depth of the open position — the next scale-in is the tier above">
                                    <span>Tier</span>
                                </Tooltip>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            [...Array(4)].map((_, i) => (
                                <TableRow key={i}>
                                    {[...Array(colCount)].map((__, j) => (
                                        <TableCell key={j}><Skeleton variant="text" /></TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : positions.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={colCount} align="center" sx={{ ...bodyCellSx, py: 4, whiteSpace: 'normal' }}>
                                    No open positions.
                                    {data?.flat_agents ? ` ${data.flat_agents} agent${data.flat_agents === 1 ? ' is' : 's are'} flat.` : ''}
                                </TableCell>
                            </TableRow>
                        ) : positions.map((p) => {
                            const src = PRICE_SOURCE_META[p.price_source] || PRICE_SOURCE_META.NONE;
                            return (
                                <TableRow key={`${p.agent_id}-${p.symbol}`} hover>
                                    {showAgentColumn && (
                                        <TableCell sx={bodyCellSx}>
                                            <Tooltip title={`Last fill: ${formatInAgentTz(p.last_fill_at, p.trading_timezone)} (${p.trading_timezone})`}>
                                                <span>{p.agent_name}</span>
                                            </Tooltip>
                                        </TableCell>
                                    )}
                                    <TableCell sx={{ ...bodyCellSx, fontWeight: 700 }}>{p.symbol}</TableCell>
                                    <TableCell sx={{ ...bodyCellSx, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        <Tooltip title={p.description || 'No description on record for this symbol'}>
                                            <span>{p.description || '—'}</span>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell sx={bodyCellSx}>
                                        <Chip label={p.direction} size="small"
                                            sx={{
                                                fontFamily, fontSize: '0.65rem', fontWeight: 700, height: 20,
                                                bgcolor: p.direction === 'LONG' ? '#dcfce7' : '#fee2e2',
                                                color: p.direction === 'LONG' ? '#15803d' : '#b91c1c',
                                            }} />
                                    </TableCell>
                                    <TableCell sx={bodyCellSx} align="right">
                                        {quantity(p.quantity, p.is_spot_crypto)}
                                    </TableCell>
                                    <TableCell sx={bodyCellSx} align="right">{money(p.cost_per_share)}</TableCell>
                                    <TableCell sx={bodyCellSx} align="right">{money(p.cost_basis)}</TableCell>
                                    <TableCell sx={bodyCellSx} align="right">
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
                                            {money(p.current_price)}
                                            <Tooltip title={src.help}>
                                                <Chip label={src.label} size="small"
                                                    sx={{ fontFamily, fontSize: '0.6rem', height: 17, bgcolor: src.color, color: src.text }} />
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ ...bodyCellSx, fontWeight: 700 }} align="right">
                                        {money(p.price_value)}
                                    </TableCell>
                                    <TableCell sx={{ ...bodyCellSx, color: signColor(p.price_change) }} align="right">
                                        {money(p.price_change)}
                                    </TableCell>
                                    <TableCell sx={{ ...bodyCellSx, color: signColor(p.price_change_pct) }} align="right">
                                        {percent(p.price_change_pct)}
                                    </TableCell>
                                    <TableCell sx={{ ...bodyCellSx, color: signColor(p.day_gain_loss) }} align="right">
                                        {money(p.day_gain_loss)}
                                    </TableCell>
                                    <TableCell sx={{ ...bodyCellSx, fontWeight: 700, color: signColor(p.gain_loss) }} align="right">
                                        {money(p.gain_loss)}
                                    </TableCell>
                                    <TableCell sx={{ ...bodyCellSx, color: signColor(p.gain_loss_pct) }} align="right">
                                        {percent(p.gain_loss_pct)}
                                    </TableCell>
                                    <TableCell sx={bodyCellSx} align="right">{percent(p.portfolio_weight_pct)}</TableCell>
                                    <TableCell sx={bodyCellSx} align="right">
                                        <Tooltip title={p.allocation_used_pct !== null && p.allocation_used_pct !== undefined
                                            ? `${percent(p.allocation_used_pct)} of this agent's ${money(p.capital_allocation, 0)} allocation`
                                            : 'No capital allocation set on this agent'}>
                                            <span>T{p.entries_in_position}</span>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {positions.length > 0 && (
                <Typography sx={{ fontFamily, fontSize: '0.72rem', color: '#94a3b8', mt: 1.5 }}>
                    Positions are derived from filled orders, the same source the agent's own Position tab and
                    decision engine read. Price Change is measured against the previous daily close.
                </Typography>
            )}
        </Box>
    );
};

export default AIAgentPositionsPage;
